import React, { useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ApiStatus } from '@gift/types/generic/ApiStatus';
import { reportError } from '@gift/common/helpers/logging';
import { Button } from '@gift/ui/components/_buttons/button/Button';
import { Spinner } from '@gift/ui/components/loaders/spinner/Spinner';
import { Alert } from '@gift/ui/components/alert/Alert';
import { AppDispatch, AppThunk, WebsiteState } from '@gift/website-state/store';
import { pushProject } from '@gift/website-state/apis/pushProject';
import { fireOrderEventToSite } from '@gift/common/site/siteEventBindings';

const _OrderButton = ({ placeOrder, pushStatus, authStatus }: Props) => {

    const [orderStatus, setOrderStatus] = useState<ApiStatus>('ready');
    const requestStatuses = [pushStatus, authStatus, orderStatus];
    const { t } = useTranslation();

    const onClick = async () => {
        setOrderStatus('pending');
        try {
            await placeOrder();
            setOrderStatus('ready');
        } catch (e) {
            setOrderStatus('error');
            reportError(e);
        }
    };

    if (requestStatuses.includes('pending')) {
        return (
            <Button disabled className='idt-order-button idt-order-button--disabled idt-cursor-wait'
                title={t('order_button.title')}>
                <Spinner overlaySize='auto' spinnerSize='small' className='idt-mr-2' color='white' />
                {t('order_button.text')}
            </Button>
        );
    }

    return (
        <>
            <Button onClick={onClick} className='idt-order-button' title={t('order_button.title')}>
                {t('order_button.text')}
            </Button>

            {requestStatuses.includes('error') && (
                <Alert icon='alert-triangle-exclamation' type='error' className='idt-mt-6'>
                    {t('order_button.error_message')}
                </Alert>
            )}
        </>
    );
};

type Props = {
    placeOrder: () => Promise<void>,
    pushStatus: ApiStatus,
    authStatus: ApiStatus
};

const mapStateToProps = (state: WebsiteState) => ({
    pushStatus: state.project.pushStatus,
    authStatus: state.project.authStatus
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
    placeOrder: async () => await dispatch(submitOrder)
});

export const OrderButton = connect(
    mapStateToProps,
    mapDispatchToProps
)(_OrderButton);

const submitOrder: AppThunk = async (dispatch, getState) => {

    try {

        const result = unwrapResult(await dispatch(pushProject()));
        const state = getState();

        return Promise.resolve(fireOrderEventToSite({
            name: 'order',
            projectId: state.project.privateId!,
            snapshotId: result.snapshotId
        }));

    } catch (e) {
        reportError(e);
    }
};
